import { Flex, TableContainer, Table, Thead, Tr, Th, Tbody, Td, Link as ChakraLink } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab, CopyButton } from '../../../../components/ActionButton'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { FilterBar, FilterSearch } from '../../../../components/FilterBar'
import { TableSkeletonRows } from '../../../../components/Loading'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../../components/paginator'
import QrCodeModal from '../../../../components/qrCodeModal/QrCodeModal'
import ShortText from '../../../../components/ShortText'
import { TableEmptyRow, TableErrorRow } from '../../../../components/TableEmptyRow'
import { formatDateTime, useHasRole, useAppToast } from '../../../../helpers'
import UrlShortenerService, { IUrlShortener } from '../../../../services/UrlShortener/UrlShortenerService'
import UtmGeneratorFormModal from './UtmGeneratorFormModal'
import { UtmGeneratorViewModal } from './UtmGeneratorViewModal'

interface IUtmGeneratorPage {
  used_by: 'commercial' | 'marketing'
}

const URL_INVALIDA_AVISO = 'A URL cadastrada é inválida, então os UTMs não foram aplicados'

const isValidUrl = (value: string) => {
  try {
    new URL(value)
    return true
  } catch {
    return false
  }
}

function UtmGeneratorPage({ used_by }: IUtmGeneratorPage) {
  const [urls, setUrls] = useState<IUrlShortener[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [filters, setFilters] = useState({
    length: DEFAULT_PAGE_SIZE,
    search: { value: '' },
    created_at: {
      from: '',
      to: ''
    }
  })
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [showFormModal, setShowFormModal] = useState(false)
  const [showingRecord, setShowingRecord] = useState<number | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null }>({ open: false, id: null })
  const [qrModal, setQrModal] = useState<{ url: string; title?: string } | null>(null)
  const [copyingData, setCopyingData] = useState<Partial<IUrlShortener> | undefined>(undefined)
  const [editingData, setEditingData] = useState<IUrlShortener | null>(null)
  const toast = useAppToast()
  const hasRole = useHasRole()
  const permissions = {
    addUrl: used_by === 'commercial' ? hasRole('add_url') : hasRole('add_marketing_url'),
    editUrl: used_by === 'commercial' ? hasRole('edit_url') : hasRole('edit_marketing_url'),
    deleteUrl: used_by === 'commercial' ? hasRole('delete_url') : hasRole('delete_marketing_url')
  }

  const fetchUrls = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const data = await UrlShortenerService.list(
        used_by,
        filters.search.value,
        page,
        filters.length,
        filters.created_at.from,
        filters.created_at.to
      )
      setTotalPages(Math.ceil(data.recordsFiltered / filters.length))
      setUrls(data.data)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar as URLs', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUrls()
  }, [page, used_by])

  useEffect(() => {
    !showingRecord && fetchUrls()
  }, [showingRecord])

  useEffect(() => {
    // A busca já chega debounced do `FilterSearch`.
    fetchUrls()
  }, [filters.search.value, filters.created_at.from, filters.created_at.to, filters.length])

  const handleDelete = async (id: number) => {
    setDeleteDialog({ open: true, id })
  }

  const confirmDelete = async () => {
    if (!deleteDialog.id) return
    try {
      await UrlShortenerService.delete(deleteDialog.id)
      toast.success('URL removida com sucesso')
      fetchUrls()
    } catch (e) {
      toast.error('Erro ao remover URL', e)
    } finally {
      setDeleteDialog({ open: false, id: null })
    }
  }

  const handleCopy = (url: IUrlShortener) => {
    setCopyingData(url)
    setShowFormModal(true)
  }

  const handleEdit = (url: IUrlShortener) => {
    setEditingData(url)
    setShowFormModal(true)
  }

  const handleCloseModal = () => {
    setShowFormModal(false)
    setCopyingData(undefined)
    setEditingData(null)
  }

  // Monta a URL de destino com os UTMs. Uma URL inválida no banco faria o `new URL` estourar: aí
  // o que se copia é o que está gravado, e o toast avisa que os UTMs não entraram — copiar a URL
  // crua em silêncio é a pior das saídas.
  const getDestinationUrl = (url: IUrlShortener) => {
    try {
      const urlObj = new URL(url.original_url)
      if (url.utm_campaign) urlObj.searchParams.set('utm_campaign', url.utm_campaign)
      if (url.utm_source) urlObj.searchParams.set('utm_source', url.utm_source)
      if (url.utm_medium) urlObj.searchParams.set('utm_medium', url.utm_medium)
      if (url.utm_content) urlObj.searchParams.set('utm_content', url.utm_content)
      if (url.utm_term) urlObj.searchParams.set('utm_term', url.utm_term)

      return urlObj.toString()
    } catch {
      return url.original_url
    }
  }

  const getFullShortUrl = (short_path: string) => `https://abra.com.br/short/${short_path}`

  return (
    <>
      <FilterBar
        onClear={() => {
          setFilters({ ...filters, search: { value: '' } })
          setPage(1)
        }}
        isDirty={filters.search.value !== ''}
      >
        <FilterSearch
          value={filters.search.value}
          onChange={(value) => {
            setFilters({ ...filters, search: { value } })
            setPage(1)
          }}
          placeholder="Buscar por URL"
        />
      </FilterBar>
      <TableContainer>
        <Table padding="0" mt="5">
          <Thead>
            <Tr>
              <Th>Título</Th>
              <Th>URL Curta</Th>
              <Th>URL de Destino</Th>
              <Th>Criado por</Th>
              <Th>Data de Criação</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={6} />
            ) : loadError ? (
              <TableErrorRow colSpan={6} entity="URLs" feminine onRetry={fetchUrls} />
            ) : urls.length === 0 ? (
              <TableEmptyRow colSpan={6} entity="URL" feminine />
            ) : (
              urls.map((url) => (
                <Tr key={url.id}>
                  <Td>
                    <ShortText text={url.title} beforeChars={30} afterChars={0} />
                  </Td>
                  <Td>
                    <Flex alignItems="center" gap={2}>
                      <ChakraLink
                        href={getFullShortUrl(url.short_path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="blue.500"
                      >
                        {getFullShortUrl(url.short_path)}
                      </ChakraLink>
                      {/* xs de propósito: acompanha o texto da URL, não é a coluna de ações */}
                      <CopyButton
                        value={getFullShortUrl(url.short_path)}
                        entity="URL curta"
                        feminine
                        size="xs"
                        mr={0}
                      />
                    </Flex>
                  </Td>
                  <Td>
                    <Flex alignItems="center" gap={2}>
                      <ShortText text={url.original_url} />
                      <CopyButton
                        value={getDestinationUrl(url)}
                        entity="URL de destino"
                        feminine
                        description={isValidUrl(url.original_url) ? undefined : URL_INVALIDA_AVISO}
                        size="xs"
                        mr={0}
                      />
                    </Flex>
                  </Td>
                  <Td>{url.user.name}</Td>
                  <Td>{formatDateTime(url.created_at)}</Td>
                  <Td>
                    <ActionButton
                      action="qrcode"
                      onClick={() => setQrModal({ url: getFullShortUrl(url.short_path), title: url.title })}
                    />
                    {permissions.editUrl && <ActionButton action="edit" onClick={() => handleEdit(url)} />}
                    {permissions.addUrl && <ActionButton action="duplicate" onClick={() => handleCopy(url)} />}
                    <ActionButton action="view" label="Visualizar" onClick={() => setShowingRecord(url.id)} />
                    {permissions.deleteUrl && <ActionButton action="delete" onClick={() => handleDelete(url.id)} />}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
        pageSize={filters.length}
        onPageSizeChange={(pageSize) => {
          setFilters({ ...filters, length: pageSize })
          setPage(1)
        }}
      />
      {permissions.addUrl && <ActionFab label="Adicionar URL curta" onClick={() => setShowFormModal(true)} />}
      <UtmGeneratorFormModal
        isOpen={showFormModal}
        onClose={handleCloseModal}
        onSaved={fetchUrls}
        used_by={used_by}
        initialData={copyingData}
        editing={editingData}
      />
      <ConfirmDialog
        isOpen={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
        onConfirm={confirmDelete}
        action="delete"
        entity="URL curta"
        feminine
      />
      <QrCodeModal url={qrModal?.url ?? null} title={qrModal?.title} onClose={() => setQrModal(null)} />
      <UtmGeneratorViewModal id={showingRecord} onClose={() => setShowingRecord(null)} />
    </>
  )
}

export default UtmGeneratorPage
